import { Box } from '@material-ui/core';
import { LenderName, PaymentMethodType } from '@nemuru-eng/js-api-client';

import { ConditionsGroupCardDetail } from '@/components/modules/Application/components/PaymentMethod/_shared/components/ConditionsGroupCard/ConditionsGroupCardDetail';
import { LenderIcons } from '@/components/modules/Application/components/PaymentMethod/_shared/components/LenderIcons';
import { LoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/ConditionsGroup/LoanConditionsGroup';
import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { areConditionsEqual } from '@/components/modules/Application/components/PaymentMethod/_shared/utils/areConditionsEqual';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { ReadOnlyDataRow } from '@/components/shared/components/NewContextForms/shared/ReadOnlyDataRow';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface ConditionsGroupCardProps<T extends AbstractLoanPaymentMethod, U extends LoanConditionsGroup> {
  paymentMethodEntity: T;
  conditionsGroup: U;
  isDetailOpen: boolean;
  selectable: boolean;
  showTerm?: boolean;
  strikethrough?: boolean;
}

export const ConditionsGroupCard = <T extends AbstractLoanPaymentMethod, U extends LoanConditionsGroup>({
  paymentMethodEntity,
  conditionsGroup,
  isDetailOpen,
  selectable,
  showTerm,
  strikethrough,
}: ConditionsGroupCardProps<T, U>) => {
  const { palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const { updatePaymentMethodEntity } = usePaymentMethods();

  const bestCandidateConditions = conditionsGroup.getCandidateConditions();

  const isSelected = areConditionsEqual(
    paymentMethodEntity.selectedConditionsGroup?.getCandidateConditions(),
    bestCandidateConditions,
  );

  const dimIfNotSelectedStyle = {
    opacity: !selectable || isSelected ? 1 : 0.5,
  };

  const borderStyle = strikethrough
    ? 'solid 1px red'
    : selectable && isSelected
    ? `solid 2px ${palette.primary.main}`
    : selectable
    ? 'solid 2px rgba(0,0,0,0)'
    : 'solid 1px rgba(0,0,0,0)';

  const showMerchantCost = paymentMethodEntity.paymentMethodType !== PaymentMethodType.AUTO_LOAN;

  return (
    <CustomPaper
      key={bestCandidateConditions.pricingId}
      style={{
        border: borderStyle,
        transition: 'all 0.15s ease',
        padding: '12px 16px 16px 16px',
      }}
      fill={strikethrough ? 'error.light' : !selectable ? 'paper' : undefined}
    >
      <Box display="flex" flexDirection={'column'} gridGap={8} height={'100%'}>
        <FlexColumn gridGap={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={'center'}
            style={dimIfNotSelectedStyle}
            width={'100%'}
          >
            <Font variant="h3">
              {conditionsGroup.instalmentTotalAmount().hasMultiple
                ? `${getTextIdTranslation('FROM_AMOUNT').toLowerCase()} `
                : ''}
              {conditionsGroup.instalmentTotalAmount().string}
            </Font>
            <LenderIcons
              allLenderNames={conditionsGroup.providerNames() as LenderName[]}
              activeLenderNames={conditionsGroup.providerNames() as LenderName[]}
              size={20}
            />
          </Box>
          {showTerm && (
            <Font variant="h5" color="textPrimary">
              {getTextIdTranslation('IN')}{' '}
              {`${conditionsGroup.getCandidateConditions().term?.toString()} ${getTextIdTranslation('MONTHS')}`}
            </Font>
          )}
        </FlexColumn>
        <Box display="flex" flexDirection={'column'} flexGrow={1} style={dimIfNotSelectedStyle}>
          <ReadOnlyDataRow
            underlined={false}
            label={'TIN'}
            value={`${
              conditionsGroup.annualInterest?.hasMultiple ? `${getTextIdTranslation('FROM_AMOUNT').toLowerCase()} ` : ''
            }${conditionsGroup.annualInterest?.string}`}
          />
          <ReadOnlyDataRow
            underlined={false}
            label={'TAE'}
            value={`${conditionsGroup.apr?.hasMultiple ? `${getTextIdTranslation('FROM_AMOUNT').toLowerCase()} ` : ''}${
              conditionsGroup.apr?.string
            }`}
          />
          <ReadOnlyDataRow
            underlined={false}
            label={
              conditionsGroup.setupFee?.value && conditionsGroup.setupFee.value > 0
                ? `SETUP_FEE_SHORT_${conditionsGroup.setupFee?.type}`
                : 'SETUP_FEE_SHORT'
            }
            value={
              conditionsGroup.setupFee?.value && conditionsGroup.setupFee.value > 0
                ? `${
                    conditionsGroup.setupFee?.hasMultiple ? `${getTextIdTranslation('FROM_AMOUNT').toLowerCase()} ` : ''
                  }${conditionsGroup.setupFee?.string}`
                : '0,00 %'
            }
          />
          {showMerchantCost && (
            <ReadOnlyDataRow
              underlined={false}
              label={'MERCHANT_COST'}
              value={`${
                conditionsGroup.merchantDiscountPercentage?.hasMultiple
                  ? `${getTextIdTranslation('FROM_AMOUNT').toLowerCase()} `
                  : ''
              }${
                conditionsGroup.merchantDiscountPercentage?.value ||
                conditionsGroup.merchantDiscountPercentage?.hasMultiple
                  ? conditionsGroup.merchantDiscountPercentage?.string
                  : getTextIdTranslation('FREE')
              }`}
            />
          )}
          {!!conditionsGroup.gracePeriod && conditionsGroup.gracePeriod.value > 0 && (
            <ReadOnlyDataRow
              underlined={false}
              label={'GRACE_PERIOD'}
              value={`${
                conditionsGroup.gracePeriod.hasMultiple ? `${getTextIdTranslation('FROM_AMOUNT').toLowerCase()} ` : ''
              }${conditionsGroup.gracePeriod.string} ${getTextIdTranslation('MONTHS').toLowerCase()}`}
            />
          )}
        </Box>
        {isDetailOpen && (
          <Box style={dimIfNotSelectedStyle}>
            <ConditionsGroupCardDetail conditionsGroup={conditionsGroup} showMerchantCost={showMerchantCost} />
          </Box>
        )}
        {selectable && (
          <Box mt={1}>
            {isSelected ? (
              <StandardButton size="small" variant="text" color="primary">
                <Icon style={{ paddingRight: 8 }}>check</Icon>
                {getTextIdTranslation('SELECTED')}
              </StandardButton>
            ) : (
              <StandardButton
                size="small"
                variant="contained"
                color="inherit"
                onClick={() => {
                  updatePaymentMethodEntity(paymentMethodEntity.setSelectedConditionsGroup(conditionsGroup));
                }}
              >
                {getTextIdTranslation('SELECT')}
              </StandardButton>
            )}
          </Box>
        )}
      </Box>
    </CustomPaper>
  );
};
